import type { BundleInfo, PartialContext } from '@ms-cloudpack/api-server';
import type { PackageJson, GeneratedPackageSettings } from '@ms-cloudpack/common-types';
import { ensureGeneratedSettingsForPackage } from '@ms-cloudpack/config';
import type { GeneratedPackageChanges } from './types/GeneratedPackageChanges.js';

/**
 * Check for dynamic `import()` statements of relative paths, and add any files where those are
 * found to `GeneratedPackageSettings.dynamicImports`.
 */
export async function evaluateDynamicImports(
  options: {
    info: BundleInfo;
    definition: PackageJson;
    packagePath: string;
  },
  context: PartialContext<'packages', 'config'>,
): Promise<{
  dynamicImportsChanges: GeneratedPackageChanges[];
  shouldReevaluate: boolean;
  shouldRefresh: boolean;
}> {
  const { info, definition, packagePath } = options;
  const { packages, session } = context;
  const { config } = session;

  const { name: packageName, version: packageVersion } = definition;
  const displayName = `${packageName}@${packageVersion}`;
  if (!packageName || !packageVersion) {
    throw new Error(`Package "${displayName}" is missing a name or version.`);
  }

  const dynamicImportsChanges: GeneratedPackageChanges[] = [];
  let shouldReevaluate = false;
  let shouldRefresh = false;
  let generatedPackageSettings: GeneratedPackageSettings | undefined;

  for (const entryInfo of Object.values(info)) {
    if (!entryInfo) {
      continue; // This is to make type-checking happy
    }
    for (const dynamicImport of entryInfo.dynamicImports || []) {
      if (dynamicImport.packageName !== '.') {
        continue;
      }

      const { importOrigin } = dynamicImport;
      // TODO: does this actually need to re-evaluate if there are no new imports?
      shouldReevaluate = true;
      generatedPackageSettings ??= await ensureGeneratedSettingsForPackage({ packagePath }, { config, packages });
      if (generatedPackageSettings?.dynamicImports?.includes(importOrigin)) {
        continue;
      }

      // We are missing an import path. We need to add this entry to the exports map in package settings.
      generatedPackageSettings.dynamicImports ??= [];
      generatedPackageSettings.dynamicImports.push(importOrigin);
      shouldRefresh = true;

      dynamicImportsChanges.push({
        name: packageName,
        version: packageVersion,
        path: packagePath,
        changes: [
          {
            type: 'added-dynamic-import',
            change: importOrigin,
            reason: `Found non-literal path in import().`,
          },
        ],
      });
    }
  }

  return { dynamicImportsChanges, shouldReevaluate, shouldRefresh };
}
